import moment from "moment";
import "moment/locale/ru";
import { Calendar } from "@fullcalendar/core";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import ruLocale from "@fullcalendar/core/locales/ru";

const escapeHtml = (value) => {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const toInputValue = (value) => {
  if (!value) return "";
  if (window.moment) {
    return window.moment(value).format("YYYY-MM-DDTHH:mm");
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const formatDateTime = (value) => {
  if (!value) return "";
  if (window.moment) {
    return window.moment(value).format("DD.MM.YYYY HH:mm");
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(dt.getDate())}.${pad(dt.getMonth() + 1)}.${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const parseDateTimeValue = (value) => {
  if (!value) return null;

  if (window.moment && window.moment.isMoment(value)) {
    return value.clone();
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return window.moment ? window.moment(value) : new Date(value.getTime());
  }

  const raw = String(value || "").trim();
  if (!raw) return null;

  if (window.moment) {
    const parsed = window.moment(
      raw,
      ["DD.MM.YYYY HH:mm", "YYYY-MM-DDTHH:mm", "YYYY-MM-DD HH:mm:ss", window.moment.ISO_8601],
      true
    );
    if (parsed.isValid()) {
      return parsed;
    }
    const fallbackDate = new Date(raw);
    if (!Number.isNaN(fallbackDate.getTime())) {
      return window.moment(fallbackDate);
    }
    return null;
  }

  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const dt = new Date(normalized);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const toPickerValue = (value) => {
  const parsed = parseDateTimeValue(value);
  if (!parsed) return "";

  if (window.moment && window.moment.isMoment(parsed)) {
    return parsed.format("DD.MM.YYYY HH:mm");
  }

  return formatDateTime(parsed);
};

const toPayloadDateTime = (value) => {
  const parsed = parseDateTimeValue(value);
  if (!parsed) return "";

  if (window.moment && window.moment.isMoment(parsed)) {
    return parsed.format("YYYY-MM-DDTHH:mm");
  }

  return toInputValue(parsed);
};

const initDateTimePicker = (inputEl) => {
  if (
    !inputEl ||
    !window.jQuery ||
    !window.jQuery.fn ||
    typeof window.jQuery.fn.daterangepicker !== "function"
  ) {
    return;
  }

  const $input = window.jQuery(inputEl);
  const existing = $input.data("daterangepicker");
  if (existing) {
    existing.remove();
  }

  const localeBase = typeof window.getDaterangepickerLocale === "function"
    ? window.getDaterangepickerLocale()
    : {};

  $input.daterangepicker({
    singleDatePicker: true,
    showDropdowns: true,
    autoApply: true,
    autoUpdateInput: false,
    timePicker: true,
    timePicker24Hour: true,
    timePickerSeconds: false,
    locale: {
      ...localeBase,
      format: "DD.MM.YYYY HH:mm",
    },
    parentEl: "#scheduleEventModal",
  });

  $input.on("apply.daterangepicker", (ev, picker) => {
    $input.val(picker.startDate.format("DD.MM.YYYY HH:mm"));
  });
};

const setDateTimeFieldValue = (inputEl, value) => {
  if (!inputEl) return;

  const displayValue = toPickerValue(value);
  inputEl.value = displayValue;

  if (
    window.jQuery &&
    window.jQuery.fn &&
    typeof window.jQuery.fn.daterangepicker === "function"
  ) {
    const picker = window.jQuery(inputEl).data("daterangepicker");
    if (picker && window.moment) {
      const parsed = parseDateTimeValue(displayValue);
      if (parsed && window.moment.isMoment(parsed)) {
        picker.setStartDate(parsed.clone());
      }
    }
  }
};

const parseInlineDate = () => {
  const input = document.getElementById("inlinewrap1");
  const value = input ? input.value : "";
  if (!value) return null;
  if (window.moment) {
    const parsed = window.moment(value, "DD/MM/YYYY", true);
    return parsed.isValid() ? parsed : null;
  }
  const parts = value.split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
  if (!dd || !mm || !yyyy) return null;
  return new Date(yyyy, mm - 1, dd);
};

const buildEventHtml = (info) => {
  const props = info.event.extendedProps || {};
  const badge = props.badge
    ? `<span class="position-absolute top-0 end-0 badge ${escapeHtml(props.badgeClass || "bg-primary")} p-1 m-1"><small>${escapeHtml(props.badge)}</small></span>`
    : "";

  const timeText = info.timeText ? `<p class="mb-0 small fw-medium">${escapeHtml(info.timeText)}</p>` : "";

  const avatars = Array.isArray(props.avatars) ? props.avatars : (props.avatars ? [props.avatars] : []);
  let avatarHtml = "";
  if (avatars.length > 0) {
    avatarHtml = `<div class="col-auto">${avatars.slice(0, 3)
      .map((src) => `<img src="${escapeHtml(src)}" class="avatar avatar-20 rounded-circle" alt="">`)
      .join(" ")}</div>`;
  }

  const iconHtml = !avatarHtml && props.icon
    ? `<div class="col-auto"><i class="${escapeHtml(props.icon)}"></i></div>`
    : "";

  const subtitle = props.subtitle ? `<p class="mb-0 opacity-75 small text-truncated">${escapeHtml(props.subtitle)}</p>` : "";
  const description = props.description ? `<p class="mb-0 opacity-75 small text-truncated">${escapeHtml(props.description)}</p>` : "";

  return {
    html: `
      ${badge}
      ${timeText}
      <div class="row gx-2 align-items-center">
        ${avatarHtml || iconHtml}
        <div class="col">${escapeHtml(info.event.title || "")}</div>
      </div>
      ${subtitle || description}
    `,
  };
};

const parseJsonResponse = async (response) => {
  const body = await response.text();
  let data = null;

  if (body) {
    try {
      data = JSON.parse(body);
    } catch (err) {
      throw new Error(`invalid_json_${response.status}`);
    }
  }

  if (!response.ok) {
    throw new Error(data?.error || `http_${response.status}`);
  }

  if (!data || typeof data !== "object") {
    throw new Error("empty_response");
  }

  return data;
};

export function initSchedule() {
  if (!window.moment) {
    window.moment = moment;
    window.moment.locale("ru");
  }
  const root = document.getElementById("schedulePage");
  if (!root) return;

  const eventsUrl = root.dataset.eventsUrl;
  const createUrl = root.dataset.createUrl;
  const updateUrl = root.dataset.updateUrl;
  const deleteUrl = root.dataset.deleteUrl;
  const csrfToken = document.getElementById("scheduleCsrf")?.value || "";

  const calendarEl = document.getElementById("calendar");
  if (!calendarEl) return;

  const modalEl = document.getElementById("scheduleEventModal");
  const formEl = document.getElementById("scheduleEventForm");
  const modal = modalEl && window.bootstrap ? new window.bootstrap.Modal(modalEl) : null;

  const toggleCustom = document.getElementById("scheduleToggleCustom");
  const toggleService = document.getElementById("scheduleToggleService");

  const fieldId = document.getElementById("scheduleEventId");
  const fieldTitle = document.getElementById("scheduleEventTitle");
  const fieldStart = document.getElementById("scheduleEventStart");
  const fieldEnd = document.getElementById("scheduleEventEnd");
  const fieldColor = document.getElementById("scheduleEventColor");
  const fieldStatus = document.getElementById("scheduleEventStatus");
  const fieldLocation = document.getElementById("scheduleEventLocation");
  const fieldDescription = document.getElementById("scheduleEventDescription");
  const fieldAvatars = document.getElementById("scheduleEventAvatars");
  const fieldMeeting = document.getElementById("scheduleEventMeeting");
  const fieldAllDay = document.getElementById("scheduleEventAllDay");
  const deleteBtn = document.getElementById("scheduleEventDelete");
  const saveBtn = document.getElementById("scheduleEventSave");
  const detailsWrap = document.getElementById("scheduleEventDetails");
  const detailsBody = document.getElementById("scheduleEventDetailsBody");
  const agentCheckboxes = Array.from(document.querySelectorAll(".schedule-agent-checkbox"));
  const agentSearch = document.getElementById("scheduleAgentSearch");
  const agentItems = Array.from(document.querySelectorAll(".schedule-agent-item"));
  const agentEmpty = document.getElementById("scheduleAgentEmpty");

  initDateTimePicker(fieldStart);
  initDateTimePicker(fieldEnd);

  const countEls = {
    total: document.querySelector('[data-schedule-count="total"]'),
    custom: document.querySelector('[data-schedule-count="custom"]'),
    service: document.querySelector('[data-schedule-count="service"]'),
    today: document.querySelector('[data-schedule-count="today"]'),
  };

  let lastEvents = [];

  const setCounts = (events) => {
    const total = events.length;
    const custom = events.filter((e) => (e.extendedProps?.source || "custom") === "custom").length;
    const service = events.filter((e) => e.extendedProps?.source === "service_request").length;

    let todayCount = 0;
    const today = window.moment ? window.moment() : new Date();
    events.forEach((event) => {
      if (!event.start) return;
      if (window.moment) {
        if (window.moment(event.start).isSame(today, "day")) {
          todayCount += 1;
        }
      } else {
        const dt = new Date(event.start);
        if (!Number.isNaN(dt.getTime())) {
          if (dt.toDateString() === new Date().toDateString()) {
            todayCount += 1;
          }
        }
      }
    });

    if (countEls.total) countEls.total.textContent = String(total);
    if (countEls.custom) countEls.custom.textContent = String(custom);
    if (countEls.service) countEls.service.textContent = String(service);
    if (countEls.today) countEls.today.textContent = String(todayCount);
  };

  const applyFilters = () => {
    const showCustom = toggleCustom ? toggleCustom.checked : true;
    const showService = toggleService ? toggleService.checked : true;
    calendar.getEvents().forEach((event) => {
      const source = event.extendedProps?.source || "custom";
      const shouldShow = source === "service_request" ? showService : showCustom;
      event.setProp("display", shouldShow ? "auto" : "none");
    });
  };

  const setFormDisabled = (disabled) => {
    [
      fieldTitle,
      fieldStart,
      fieldEnd,
      fieldColor,
      fieldStatus,
      fieldLocation,
      fieldDescription,
      fieldAvatars,
      fieldMeeting,
      fieldAllDay,
      agentSearch,
    ].forEach((el) => {
      if (el) el.disabled = disabled;
    });
    agentCheckboxes.forEach((el) => {
      el.disabled = disabled;
    });
  };

  const filterAgents = () => {
    if (!agentSearch || agentItems.length === 0) return;
    const query = agentSearch.value.trim().toLowerCase();
    let visible = 0;
    agentItems.forEach((item) => {
      const hay = (item.dataset.agentSearch || "").toLowerCase();
      const show = query === "" || hay.includes(query);
      item.parentElement?.classList.toggle("d-none", !show);
      if (show) visible += 1;
    });
    if (agentEmpty) {
      agentEmpty.classList.toggle("d-none", visible > 0);
    }
  };

  const setAgentSelections = (ids) => {
    const idSet = new Set((ids || []).map((id) => String(id)));
    agentCheckboxes.forEach((el) => {
      el.checked = idSet.has(String(el.value));
    });
  };

  const hasDateChanged = (nextDate, prevDate) => {
    const nextTs = nextDate instanceof Date ? nextDate.getTime() : null;
    const prevTs = prevDate instanceof Date ? prevDate.getTime() : null;
    return nextTs !== prevTs;
  };

  const hasTimingChanged = (event, oldEvent) => {
    if (!oldEvent) return false;
    if (Boolean(event.allDay) !== Boolean(oldEvent.allDay)) {
      return true;
    }
    return hasDateChanged(event.start, oldEvent.start) || hasDateChanged(event.end, oldEvent.end);
  };

  const collectAgentIds = () => {
    return agentCheckboxes
      .filter((el) => el.checked)
      .map((el) => el.value)
      .filter(Boolean);
  };

  const openModal = (payload = {}) => {
    if (!modal) return;
    if (formEl) formEl.reset();
    agentCheckboxes.forEach((el) => { el.checked = false; });
    if (agentSearch) {
      agentSearch.value = "";
      filterAgents();
    }

    const {
      id = "",
      title = "",
      start = "",
      end = "",
      allDay = false,
      colorClass = "bg-theme-1-space text-white",
      status = "planned",
      location = "",
      description = "",
      avatars = "",
      meetingUrl = "",
      participantIds = [],
      readOnly = false,
      detailsHtml = "",
    } = payload;

    if (fieldId) fieldId.value = id;
    if (fieldTitle) fieldTitle.value = title;
    setDateTimeFieldValue(fieldStart, start);
    setDateTimeFieldValue(fieldEnd, end);
    if (fieldColor) fieldColor.value = colorClass;
    if (fieldStatus) fieldStatus.value = status;
    if (fieldLocation) fieldLocation.value = location;
    if (fieldDescription) fieldDescription.value = description;
    if (fieldAvatars) fieldAvatars.value = avatars;
    if (fieldMeeting) fieldMeeting.value = meetingUrl;
    if (fieldAllDay) fieldAllDay.checked = Boolean(allDay);

    setAgentSelections(participantIds);
    setFormDisabled(readOnly);

    if (detailsWrap && detailsBody) {
      detailsBody.innerHTML = detailsHtml || "";
      detailsWrap.classList.toggle("d-none", !detailsHtml);
    }

    if (deleteBtn) {
      deleteBtn.style.display = !readOnly && id ? "inline-flex" : "none";
    }
    if (saveBtn) {
      saveBtn.style.display = readOnly ? "none" : "inline-flex";
    }

    modal.show();
  };

  const calendar = new Calendar(calendarEl, {
    plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin],
    initialView: "timeGridWeek",
    height: "auto",
    nowIndicator: true,
    selectable: true,
    editable: true,
    eventStartEditable: true,
    eventDurationEditable: true,
    eventResizableFromStart: true,
    dayMaxEvents: true,
    locale: "ru",
    locales: [ruLocale],
    firstDay: 1,
    headerToolbar: {
      left: "prev,next today",
      center: "title",
      right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
    },
    buttonText: {
      today: "Сегодня",
      month: "Месяц",
      week: "Неделя",
      day: "День",
      list: "Список",
    },
    eventTimeFormat: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
    eventContent: buildEventHtml,
    eventDidMount: (info) => {
      const isServiceEvent = info.event.extendedProps?.source === "service_request";
      const isEditable = !isServiceEvent && info.event.editable !== false;
      info.el.style.cursor = isEditable ? "grab" : "default";
      if (isEditable) {
        info.el.title = "Перетащите встречу для изменения времени";
      }
    },
    eventAllow: (dropInfo, draggedEvent) => {
      if (draggedEvent.extendedProps?.source === "service_request") {
        return false;
      }
      return draggedEvent.editable !== false;
    },
    events: async (info, successCallback, failureCallback) => {
      try {
        const url = new URL(eventsUrl, window.location.origin);
        url.searchParams.set("start", info.startStr);
        url.searchParams.set("end", info.endStr);
        const res = await fetch(url.toString(), { credentials: "same-origin" });
        const data = await parseJsonResponse(res);
        if (!data || data.ok === false) {
          throw new Error(data?.error || "fetch_failed");
        }
        lastEvents = data.events || [];
        setCounts(lastEvents);
        successCallback(lastEvents);
        requestAnimationFrame(applyFilters);
      } catch (err) {
        console.error(err);
        failureCallback(err);
      }
    },
    select: (info) => {
      openModal({
        start: info.startStr,
        end: info.endStr,
        allDay: info.allDay,
      });
    },
    eventClick: (info) => {
      const props = info.event.extendedProps || {};
      if (props.source === "service_request") {
        const detailsHtml = `
          <div class="mb-2"><strong>Услуга:</strong> ${escapeHtml(props.product_name || info.event.title || "Запись")}</div>
          <div class="mb-2"><strong>Статус:</strong> ${escapeHtml(props.badge || props.status || "")}</div>
          <div class="mb-2"><strong>Время:</strong> ${escapeHtml(formatDateTime(info.event.start))} - ${escapeHtml(formatDateTime(info.event.end))}</div>
          <div class="mb-2"><strong>Специалист:</strong> ${escapeHtml(props.specialist_name || "—")}</div>
          <div class="mb-2"><strong>Клиент:</strong> ${escapeHtml(props.client_name || "—")}</div>
          <div class="mb-2"><strong>Телефон:</strong> ${escapeHtml(props.client_phone || "—")}</div>
          <div class="mb-2"><strong>Email:</strong> ${escapeHtml(props.client_email || "—")}</div>
          ${props.description ? `<div class="mb-2"><strong>Комментарий:</strong> ${escapeHtml(props.description)}</div>` : ""}
          ${props.manageUrl ? `<a class="btn btn-sm btn-outline-theme mt-2" href="${escapeHtml(props.manageUrl)}" target="_blank">Открыть заявку</a>` : ""}
        `;
        openModal({
          title: info.event.title,
          start: info.event.start,
          end: info.event.end,
          allDay: info.event.allDay,
          colorClass: (info.event.classNames || []).join(" "),
          readOnly: true,
          detailsHtml,
        });
        return;
      }

      const participantText = props.participants_text ? `<div class="mb-2"><strong>Участники:</strong> ${escapeHtml(props.participants_text)}</div>` : "";
      const detailsHtml = `
        <div class="mb-2"><strong>Организатор:</strong> ${escapeHtml(props.owner_name || "—")}</div>
        ${participantText}
        <div class="mb-2"><strong>Время:</strong> ${escapeHtml(formatDateTime(info.event.start))} - ${escapeHtml(formatDateTime(info.event.end))}</div>
        ${props.location ? `<div class="mb-2"><strong>Место:</strong> ${escapeHtml(props.location)}</div>` : ""}
        ${props.meeting_url ? `<div class="mb-2"><strong>Ссылка:</strong> ${escapeHtml(props.meeting_url)}</div>` : ""}
        ${props.description ? `<div class="mb-2"><strong>Описание:</strong> ${escapeHtml(props.description)}</div>` : ""}
      `;

      openModal({
        id: info.event.id,
        title: info.event.title,
        start: info.event.start,
        end: info.event.end,
        allDay: info.event.allDay,
        colorClass: (info.event.classNames || []).join(" ") || "bg-theme-1-space text-white",
        status: props.status || "planned",
        location: props.location || "",
        description: props.description || "",
        avatars: Array.isArray(props.avatars) ? props.avatars.join(", ") : "",
        meetingUrl: props.meeting_url || "",
        participantIds: props.participant_ids || [],
        readOnly: info.event.editable === false,
        detailsHtml,
      });
    },
    eventChange: (changeInfo) => {
      if (!hasTimingChanged(changeInfo.event, changeInfo.oldEvent)) {
        return;
      }
      if (changeInfo.event.extendedProps?.source === "service_request") {
        changeInfo.revert();
        return;
      }
      updateEventTime(changeInfo.event, changeInfo.revert);
    },
  });

  const updateEventTime = async (event, revert) => {
    try {
      const payload = new URLSearchParams();
      payload.set("csrf_token", csrfToken);
      payload.set("id", event.id);
      payload.set("start_at", toInputValue(event.start));
      if (event.end) {
        payload.set("end_at", toInputValue(event.end));
      }
      payload.set("all_day", event.allDay ? "1" : "0");

      const res = await fetch(updateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
        credentials: "same-origin",
      });
      const data = await parseJsonResponse(res);
      if (!data || data.ok === false) {
        throw new Error(data?.error || "update_failed");
      }
    } catch (err) {
      console.error(err);
      if (revert) revert();
    }
  };

  if (toggleCustom) {
    toggleCustom.addEventListener("change", applyFilters);
  }
  if (toggleService) {
    toggleService.addEventListener("change", applyFilters);
  }

  if (agentSearch) {
    agentSearch.addEventListener("input", filterAgents);
  }

  const createBtn = document.getElementById("scheduleCreateBtn");
  if (createBtn) {
    createBtn.addEventListener("click", () => openModal());
  }

  const quickBtn = document.getElementById("scheduleQuickCreate");
  if (quickBtn) {
    quickBtn.addEventListener("click", () => {
      const base = parseInlineDate();
      let startMoment = window.moment ? window.moment() : new Date();
      if (base) {
        startMoment = window.moment ? base.clone() : base;
      }
      const title = document.getElementById("quickEventTitle")?.value || "";
      openModal({
        title,
        start: startMoment,
        end: window.moment ? startMoment.clone().add(1, "hour") : null,
      });
    });
  }

  document.querySelectorAll(".schedule-time-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const time = btn.dataset.time || "09:00";
      const base = parseInlineDate();
      let startMoment = window.moment ? window.moment() : new Date();
      if (base) {
        startMoment = window.moment ? base.clone() : base;
      }
      if (window.moment) {
        const [h, m] = time.split(":").map((n) => parseInt(n, 10));
        startMoment.hour(h || 0).minute(m || 0).second(0);
      }
      const title = document.getElementById("quickEventTitle")?.value || "";
      openModal({
        title,
        start: startMoment,
        end: window.moment ? startMoment.clone().add(1, "hour") : null,
      });
    });
  });

  if (formEl) {
    formEl.addEventListener("submit", async (event) => {
      event.preventDefault();

      const payload = new URLSearchParams();
      payload.set("csrf_token", csrfToken);
      if (fieldId?.value) payload.set("id", fieldId.value);
      payload.set("title", fieldTitle?.value || "");
      payload.set("start_at", toPayloadDateTime(fieldStart?.value || ""));
      payload.set("end_at", toPayloadDateTime(fieldEnd?.value || ""));
      payload.set("color_class", fieldColor?.value || "");
      payload.set("status", fieldStatus?.value || "planned");
      payload.set("location", fieldLocation?.value || "");
      payload.set("description", fieldDescription?.value || "");
      payload.set("avatars", fieldAvatars?.value || "");
      payload.set("meeting_url", fieldMeeting?.value || "");
      payload.set("all_day", fieldAllDay?.checked ? "1" : "0");
      payload.append("agent_ids[]", "");
      collectAgentIds().forEach((id) => {
        payload.append("agent_ids[]", id);
      });

      const isUpdate = Boolean(fieldId?.value);
      const url = isUpdate ? updateUrl : createUrl;

      try {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
          credentials: "same-origin",
        });
        const data = await parseJsonResponse(res);
        if (!data || data.ok === false) {
          throw new Error(data?.error || "save_failed");
        }
        modal?.hide();
        calendar.refetchEvents();
      } catch (err) {
        console.error(err);
      }
    });
  }

  if (deleteBtn) {
    deleteBtn.addEventListener("click", async () => {
      if (!fieldId?.value) return;
      if (!window.confirm("Удалить встречу?")) return;

      const payload = new URLSearchParams();
      payload.set("csrf_token", csrfToken);
      payload.set("id", fieldId.value);

      try {
        const res = await fetch(deleteUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
          credentials: "same-origin",
        });
        const data = await parseJsonResponse(res);
        if (!data || data.ok === false) {
          throw new Error(data?.error || "delete_failed");
        }
        modal?.hide();
        calendar.refetchEvents();
      } catch (err) {
        console.error(err);
      }
    });
  }

  calendar.render();
}
